import { useEffect, useRef, useState } from "react";
import { Globe } from "lucide-react";

import { useT } from "../../i18n";
import { cx } from "../../lib/cx";
import { LOCALES, useLocaleStore, type Locale } from "../../store/localeStore";
import { IconButton } from "../ui/IconButton";

/**
 * Interface language picker, next to the theme toggle.
 *
 * No flags: a flag is a country, not a language, and in this product the two
 * do not line up — Russian and Kazakh are both languages of the same country.
 * The short code on the button is what a user scans for; the menu spells each
 * language in itself, because someone hunting for Kazakh is looking for
 * "Қазақша", not for whatever the current language calls it.
 */
const SHORT: Record<Locale, string> = { ru: "RU", kk: "KZ", en: "EN" };

export function LanguageControl() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);

  // Close on outside click / Esc, same behaviour as the base-map and region
  // pickers so the three controls feel like one family.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <IconButton
        label={t.header.languageLabel}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={open ? "bg-accent-subtle text-accent" : undefined}
      >
        <span className="flex items-center gap-1">
          <Globe size={18} />
          <span className="text-xs font-medium">{SHORT[locale]}</span>
        </span>
      </IconButton>

      {open && (
        <div
          role="menu"
          aria-label={t.header.languageLabel}
          className="absolute right-0 top-full z-20 mt-1 w-36 rounded-panel border border-border-subtle bg-surface-raised/95 p-1.5 shadow-md backdrop-blur"
        >
          {LOCALES.map((code) => (
            <button
              key={code}
              type="button"
              role="menuitemradio"
              aria-checked={locale === code}
              onClick={() => {
                setLocale(code);
                setOpen(false);
              }}
              className={cx(
                "flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-sm transition-colors hover:bg-surface-sunken",
                locale === code ? "text-accent" : "text-text-primary",
              )}
            >
              <span className="w-3 shrink-0">{locale === code ? "✓" : ""}</span>
              <span className="flex-1">{t.languages[code]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
